import { z } from 'zod'

// Период для планирования и графика план/факт.
// 'day'/'week' не имеют смысла для месячного плана — оставляем 'month'/'quarter'/'year'
// и 'custom' для произвольного диапазона.
export type PlanningPeriodKey = 'month' | 'quarter' | 'year' | 'custom'

// YYYY-MM (например, '2026-05'). Используется как ключ строки в manager_monthly_plans.
export const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/

export const MonthKeySchema = z.string().regex(MONTH_KEY_RE, 'Ожидался формат YYYY-MM')
export type MonthKey = z.infer<typeof MonthKeySchema>

/** Текущий месяц в формате YYYY-MM (UTC), удобно для дефолтов. */
export function currentMonthKey(now: Date = new Date()): MonthKey {
  const y = now.getUTCFullYear()
  const m = String(now.getUTCMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

/** Список месяцев YYYY-MM, перекрытых периодом (включительно).
 *  Используется на API для подбора plan/fact-строк и на графике для оси X. */
export function listMonthsInRange(fromIso: string, toIso: string): MonthKey[] {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  const out: MonthKey[] = []
  const cur = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 1))
  const end = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), 1))
  while (cur <= end) {
    const y = cur.getUTCFullYear()
    const m = String(cur.getUTCMonth() + 1).padStart(2, '0')
    out.push(`${y}-${m}`)
    cur.setUTCMonth(cur.getUTCMonth() + 1)
  }
  return out
}

/**
 * Верхняя граница плана/факта в ₽ — ёмкость Decimal(14,2). Ровно триллион
 * раньше проходил проверку и падал при записи (№148 техдолга, пакет 5). То же
 * для снимка факта в manager_plan_fact_history.
 */
export const PLANNING_AMOUNT_MAX = 999_999_999_999.99
/** Верхняя граница АКБ — количество клиентов (штуки). */
export const PLANNING_AKB_MAX = 1_000_000

// ── DTO для PATCH /planning/:managerId ──
export const UpdatePlanningSchema = z
  .object({
    // Рубли с копейками (17.09.2026): факт переносят из выписки бухгалтерии
    // («1 140 415,32»). Раньше схема пускала только целые, а поле UI выкидывало
    // запятую — сумма сохранялась в 100 раз больше. Лишние знаки после запятой
    // сервер округляет до копеек.
    planAmount: z.number().min(0).max(PLANNING_AMOUNT_MAX).optional(),
    factAmount: z.number().min(0).max(PLANNING_AMOUNT_MAX).optional(),
    // АКБ — количество клиентов (штуки).
    akbPlanCount: z.number().int().min(0).max(PLANNING_AKB_MAX).optional(),
    akbFactCount: z.number().int().min(0).max(PLANNING_AKB_MAX).optional(),
  })
  .refine(
    (v) =>
      v.planAmount !== undefined ||
      v.factAmount !== undefined ||
      v.akbPlanCount !== undefined ||
      v.akbFactCount !== undefined,
    { message: 'Укажите хотя бы одно значение: план, факт, план АКБ или факт АКБ' },
  )
export type UpdatePlanningDto = z.infer<typeof UpdatePlanningSchema>

// ── Строка таблицы /planning ──
export interface PlanningRow {
  managerId: string
  managerName: string // name || email
  managerEmail: string
  role: 'MANAGER' | 'ROP'
  /** Сумма plan по всем месяцам периода в ₽ с копейками (1140415.32). */
  planAmount: number
  /** Сумма fact по всем месяцам периода в ₽ с копейками. */
  factAmount: number
  /** plan/fact-разрез по месяцам внутри периода. Используется графиком и таблицей. */
  monthly: PlanningMonthlyEntry[]
  /** Сумма по всем КП заявок этого менеджера за период (расчётная). */
  quotedAmount: number
  /** Сумма orderedPrice × quantity по позициям, перешедшим в ORDERED в период. */
  orderedAmount: number
  /** АКБ план — суммарное кол-во клиентов по всем месяцам периода (штуки). */
  akbPlanCount: number
  /** АКБ факт — суммарное кол-во клиентов по всем месяцам периода (штуки). */
  akbFactCount: number
  /** Дата последнего изменения финансового факта за период (ISO) или null, если факт ни разу не правили. */
  factUpdatedAt: string | null
  /** Дата последнего изменения АКБ-факта за период (ISO) или null. */
  akbFactUpdatedAt: string | null
}

export interface PlanningMonthlyEntry {
  month: MonthKey
  /** ₽ с копейками. */
  planAmount: number
  /** ₽ с копейками. */
  factAmount: number
  akbPlanCount: number
  akbFactCount: number
}

// ── Точки графика ──
export interface PlanningChartSeriesPoint {
  /** Метка точки на оси X — `YYYY-MM-DD` для day-grain (месяц), `YYYY-MM` для month-grain. */
  x: string
  /** Для финансовых линий — целые ₽ (копейки на графике не нужны). */
  plan: number | null
  fact: number | null
}

export interface PlanningChartSeries {
  /** UUID менеджера или 'ALL' для агрегата «все выбранные суммарно». */
  managerId: string | 'ALL'
  /** Подпись в легенде («Иван — план», «Все — факт» и т.п.). */
  label: string
  /** Цвет линии (выдаётся бэком, чтобы план/факт одного менеджера совпадали по цвету). */
  color: string
  /** Тип линии — план или факт. */
  kind: 'plan' | 'fact'
  points: PlanningChartSeriesPoint[]
}

export interface PlanningChartResponse {
  /** 'day' — точки по дням (для period=month с линеаризацией), 'month' — по месяцам. */
  grain: 'day' | 'month'
  from: string // ISO
  to: string // ISO
  /** Финансовые линии: план/факт в ₽. */
  series: PlanningChartSeries[]
  /** Линии АКБ: план/факт количества клиентов (штуки). */
  akbSeries: PlanningChartSeries[]
}

// ── /planning список ──
export interface PlanningListResponse {
  period: { key: PlanningPeriodKey; from: string; to: string; months: MonthKey[] }
  rows: PlanningRow[]
}

// ── Рейтинг план/факт (горизонтальные бары на дашборде) ──
export interface PlanningRatingRow {
  /** managerId обычного менеджера или РОПа (для строки-команды). */
  unitId: string
  /** «Иван Петров» или «Команда РОП Сидоров». */
  label: string
  /** true — строка-агрегат команды РОПа. */
  isTeam: boolean
  /** Финансовый план/факт за период в целых ₽ (сумма по команде для isTeam). */
  planAmount: number
  factAmount: number
  /** АКБ план/факт за период — количество клиентов. */
  akbPlanCount: number
  akbFactCount: number
}

export interface PlanningRatingResponse {
  from: string // ISO
  to: string // ISO
  rows: PlanningRatingRow[]
}

// ── Личный план/факт менеджера (блок на дашборде продажника) ──
export interface MyPlanningSummary {
  /** Месяц, за который показан план/факт — `YYYY-MM` (текущий календарный). */
  periodMonth: MonthKey
  /** Финансовый план на месяц в ₽ с копейками. 0 — если админ ещё не задал. */
  planAmount: number
  /** Финансовый факт на месяц в ₽ с копейками. */
  factAmount: number
  /** Сумма выставленных КП за месяц (расчётная). */
  quotedAmount: number
  /** Сумма позиций, ушедших в заказ за месяц (расчётная). */
  orderedAmount: number
  /** АКБ план — количество клиентов на месяц (штуки). */
  akbPlanCount: number
  /** АКБ факт — количество клиентов на месяц (штуки). */
  akbFactCount: number
}
